/**
 * ETA Calculator
 * Fetches departure times from multiple sources:
 * 1. First Bus API (primary for First Essex buses)
 * 2. BODS SIRI-VM (fallback for other operators)
 * 3. GTFS timetable data (scheduled times)
 */

import { Logger } from '@utils/logger';
import { parseTimeToDate, formatTimeHHMM } from '@utils/time';
import { fetchFirstBusDepartures } from '@api/first-bus';
import { fetchVehiclesNear } from '@api/bods-siri-vm';
import { getScheduledDepartures, isGTFSDataAvailable } from '@api/bods-gtfs';
import { GeolocationService } from '@core/geolocation';
import type { Departure, Coordinates, VehicleActivity, BusStop } from '@/types';

/** Average bus speed for ETA calculations (meters per second) */
const AVERAGE_BUS_SPEED_MPS = 8; // ~29 km/h in urban areas

/**
 * Calculate departures for a bus stop using multiple data sources
 * Priority: First Bus API > BODS SIRI-VM > GTFS timetables
 * @param stop - Bus stop to get departures for
 * @param limit - Maximum number of departures to return
 */
export async function calculateDepartures(stop: BusStop, limit = 3): Promise<Departure[]> {
    // Try First Bus API first (works well for First Essex)
    Logger.info('Trying First Bus API', { atcoCode: stop.atcoCode });
    const firstBusDepartures = await fetchFirstBusDepartures(stop.atcoCode, limit);

    if (firstBusDepartures.length > 0) {
        Logger.info('Got departures from First Bus API', { count: firstBusDepartures.length });
        return enrichFirstBusDepartures(firstBusDepartures, stop.coordinates);
    }

    // Fall back to BODS SIRI-VM + GTFS
    Logger.info('First Bus returned no data, trying BODS', { atcoCode: stop.atcoCode });
    return calculateDeparturesFromBODS(stop, limit);
}

/**
 * Calculate departures using BODS data (SIRI-VM + GTFS)
 * This is the fallback when First Bus API doesn't have data
 */
async function calculateDeparturesFromBODS(stop: BusStop, limit: number): Promise<Departure[]> {
    const stopCoords = stop.coordinates;

    // Try to get scheduled departures first
    const gtfsAvailable = await isGTFSDataAvailable();
    const scheduledDepartures = gtfsAvailable
        ? await getScheduledDepartures(stop.atcoCode, limit * 2)
        : [];

    // If no scheduled departures, fall back to real-time data only
    if (scheduledDepartures.length === 0) {
        Logger.debug('No scheduled departures found, using real-time only');
        return getRealTimeOnlyDepartures(stopCoords, limit);
    }

    // Fetch real-time vehicle positions
    let vehicles: VehicleActivity[] = [];
    try {
        vehicles = await fetchVehiclesNear(stopCoords);
        Logger.debug(`Found ${vehicles.length} vehicles near stop ${stop.atcoCode}`);
    } catch (error) {
        Logger.warn('Failed to fetch real-time vehicle data', error);
        // Fall back to timetable-only
    }

    // Match vehicles to scheduled departures and calculate ETAs
    const departures: Departure[] = [];

    for (const scheduled of scheduledDepartures) {
        // Find a matching vehicle for this scheduled departure
        const matchingVehicle = findMatchingVehicle(scheduled, vehicles, stopCoords);

        if (matchingVehicle) {
            // Calculate real-time ETA based on vehicle position
            const eta = calculateETAFromVehicle(matchingVehicle, stopCoords);

            departures.push({
                line: scheduled.line,
                destination: scheduled.destination,
                expectedDeparture: formatTimeHHMM(eta),
                minutesUntil: Math.round((eta.getTime() - Date.now()) / 60000),
                status: 'on-time',
                operatorName: scheduled.operatorName,
                isRealTime: true,
                occupancy: matchingVehicle.occupancy,
            });
        } else {
            // Use scheduled time from timetable
            const scheduledTime = parseTimeToDate(scheduled.departureTime);
            const minutesUntil = Math.round((scheduledTime.getTime() - Date.now()) / 60000);

            // Skip if already departed
            if (minutesUntil < -2) continue;

            departures.push({
                line: scheduled.line,
                destination: scheduled.destination,
                expectedDeparture: formatTimeHHMM(scheduledTime),
                minutesUntil: Math.max(0, minutesUntil),
                status: 'scheduled',
                operatorName: scheduled.operatorName,
                isRealTime: false,
            });
        }

        if (departures.length >= limit) break;
    }

    // Sort by minutes until departure
    return departures.sort((a, b) => a.minutesUntil - b.minutesUntil).slice(0, limit);
}

/**
 * Find a vehicle that matches a scheduled departure
 */
function findMatchingVehicle(
    scheduled: { line: string; tripId: string },
    vehicles: VehicleActivity[],
    stopCoords: Coordinates
): VehicleActivity | null {
    for (const vehicle of vehicles) {
        // Match by line reference
        if (normalizeLineRef(vehicle.lineRef) === normalizeLineRef(scheduled.line)) {
            // Check if vehicle is approaching (within reasonable distance)
            const distance = GeolocationService.calculateDistance(
                { latitude: vehicle.latitude, longitude: vehicle.longitude },
                stopCoords
            );

            // Only consider vehicles within 3km and heading towards the stop
            if (distance < 3000) {
                return vehicle;
            }
        }
    }
    return null;
}

/**
 * Calculate ETA based on vehicle position and distance to stop
 */
function calculateETAFromVehicle(vehicle: VehicleActivity, stopCoords: Coordinates): Date {
    const distance = GeolocationService.calculateDistance(
        { latitude: vehicle.latitude, longitude: vehicle.longitude },
        stopCoords
    );

    // Estimate time based on average speed
    const etaSeconds = distance / AVERAGE_BUS_SPEED_MPS;
    const eta = new Date(Date.now() + etaSeconds * 1000);

    return eta;
}

/**
 * Get departures from real-time data only (no timetable)
 * This is a fallback when GTFS data is not available
 */
async function getRealTimeOnlyDepartures(
    stopCoords: Coordinates,
    limit: number
): Promise<Departure[]> {
    Logger.info('Fetching real-time only departures', { stopCoords });
    try {
        const vehicles = await fetchVehiclesNear(stopCoords);
        Logger.info('Fetched vehicles from SIRI-VM', { count: vehicles.length });
        const departures: Departure[] = [];

        // Group vehicles by line and get nearest one for each line
        const vehiclesByLine = new Map<string, VehicleActivity>();

        for (const vehicle of vehicles) {
            const line = normalizeLineRef(vehicle.lineRef);
            const existing = vehiclesByLine.get(line);

            if (!existing) {
                vehiclesByLine.set(line, vehicle);
            } else {
                // Keep the closer vehicle
                const existingDistance = GeolocationService.calculateDistance(
                    { latitude: existing.latitude, longitude: existing.longitude },
                    stopCoords
                );
                const newDistance = GeolocationService.calculateDistance(
                    { latitude: vehicle.latitude, longitude: vehicle.longitude },
                    stopCoords
                );
                if (newDistance < existingDistance) {
                    vehiclesByLine.set(line, vehicle);
                }
            }
        }

        // Create departures from nearest vehicles
        for (const [line, vehicle] of vehiclesByLine) {
            const eta = calculateETAFromVehicle(vehicle, stopCoords);
            const minutesUntil = Math.round((eta.getTime() - Date.now()) / 60000);

            departures.push({
                line,
                destination: vehicle.destinationName || 'Unknown',
                expectedDeparture: formatTimeHHMM(eta),
                minutesUntil: Math.max(0, minutesUntil),
                status: 'on-time',
                operatorName: undefined,
                isRealTime: true,
                occupancy: vehicle.occupancy,
            });
        }

        return departures.sort((a, b) => a.minutesUntil - b.minutesUntil).slice(0, limit);
    } catch (error) {
        Logger.warn('Failed to get real-time only departures', error);
        return [];
    }
}

/**
 * Normalize line reference for matching
 * BODS line refs may include operator prefix (e.g., "ARBB:42")
 */
function normalizeLineRef(lineRef: string): string {
    // Remove operator prefix if present
    const parts = lineRef.split(':');
    return parts[parts.length - 1].trim().toUpperCase();
}

/** Lowercase and strip punctuation/extra spaces so destination names can be compared */
function normalizeDestination(name: string): string {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9 ]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Find the live vehicle running this departure: same route AND heading to the same place.
 * Matching on route alone could pick a bus going the other way, showing its destination
 * and occupancy against this departure. First Bus names are sometimes truncated
 * ("Broomfield Hosp"), so a name that starts with the other counts as the same place.
 */
function findVehicleForDeparture(
    departure: Departure,
    vehicles: VehicleActivity[]
): VehicleActivity | undefined {
    const line = normalizeLineRef(departure.line);
    const destination = normalizeDestination(departure.destination);
    if (!destination) return undefined;

    return vehicles.find(vehicle => {
        if (normalizeLineRef(vehicle.lineRef) !== line || !vehicle.destinationName) return false;
        const vehicleDestination = normalizeDestination(vehicle.destinationName);
        return (
            vehicleDestination.startsWith(destination) || destination.startsWith(vehicleDestination)
        );
    });
}

/**
 * Enrich First Bus departures with SIRI-VM destination names and occupancy
 */
async function enrichFirstBusDepartures(
    departures: Departure[],
    stopCoords: Coordinates
): Promise<Departure[]> {
    try {
        // Fetch SIRI-VM with 3s timeout to avoid blocking
        const vehicles = await Promise.race([
            fetchVehiclesNear(stopCoords),
            new Promise<VehicleActivity[]>(resolve => setTimeout(() => resolve([]), 3000)),
        ]);

        if (vehicles.length === 0) return departures;

        return departures.map(dep => {
            const vehicle = findVehicleForDeparture(dep, vehicles);
            if (!vehicle) return dep;

            // Prefer the SIRI-VM name when it's the fuller version of the same destination
            const siriDestination = vehicle.destinationName ?? '';
            const destination =
                siriDestination.length > dep.destination.length ? siriDestination : dep.destination;

            return { ...dep, destination, occupancy: vehicle.occupancy };
        });
    } catch {
        return departures; // Graceful fallback
    }
}
